/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Card, CardContent, CardExtra } from '../../../Shared/Card'
import { Icon, Statistic, StatisticLabel, StatisticValue } from 'semantic-ui-react'
import { Link } from 'react-router-dom-v5-compat'
import React from 'react'
import { useUnavailableItemSummary } from '../../../../Home/UnavailableItems/hooks'
import { format, roundToNearestMinutes, startOfMonth } from 'date-fns'
import { useTranslation } from 'react-i18next'
import useUser from '../../../../Shared/UserProvider/useUser'
import { NoUnavailableItemsCardContent } from './NoUnavailableItemsCardContent'
import { LoadingPlaceholderCardContent } from '../../../Shared/LoadingPlaceholderCardContent'
import { fr } from 'date-fns/locale'
import { HUNDRED_THOUSAND, intlFormatMoneyWithCompact } from '../../../../../helpers/money'
import { CardTitle } from '../../../Shared/CardTitle'


const UnavailableItemsSummaryCard = () => {
    const [t] = useTranslation()
    const {user} = useUser()
    const from = startOfMonth(new Date()).toISOString()
    const to = roundToNearestMinutes(new Date(), {nearestTo: 10, roundingMethod: 'floor'}).toISOString()

    const {data: summary, isLoading} = useUnavailableItemSummary({
        from, to,
    })

    const getBorderColor = () => {
        if (isLoading) {
            return 'unset'
        }

        return summary.totalMissedQuantity === 0 ? '#21ba45' : '#db2828'
    }

    return (<Card
      css={css`
          border-top: 2px solid ${getBorderColor()};

          .card-content {
              width: 360px;
              height: 175px;
          }
      `}
    >
        {isLoading ? (
          <LoadingPlaceholderCardContent/>
        ) : (summary.totalMissedQuantity > 0 ? (
          <CardContent
            css={css`
                display: grid;
                place-items: center;
                gap: .5rem;
                ${isLoading ? '' : 'grid-template-columns: 1fr 1fr;'}
                grid-template-areas: ${isLoading ? `'title'  'a'` : `
                'title title'
                'a a'
                'b c'
                `};
            `}
          >
              <>
                  <CardTitle
                    title={format(new Date(), 'MMMM', {locale: fr})}
                  />

                  <Statistic
                    css={css`
                        grid-area: a;

                        &.ui.statistic, &.ui.statistic + .ui.statistic {
                            margin: 0;
                        }
                    `}
                    size="small"
                  >
                      <StatisticValue>
                          {intlFormatMoneyWithCompact(summary.totalMissedRevenue, user.getCompanyCurrency(), user.getCompanyLocale(), HUNDRED_THOUSAND)}
                      </StatisticValue>
                      <StatisticLabel>{t('unavailable_items.summary.total_revenue')}</StatisticLabel>
                  </Statistic>

                  <Statistic
                    css={css`
                        grid-area: b;
                        align-self: start;
                        font-size: 8pt;

                        &.ui.statistic, &.ui.statistic + .ui.statistic {
                            margin: 0;
                        }
                    `}
                    color={summary.totalMissedRevenueUnavailable > 0 ? 'red' : undefined}
                    size="mini"
                  >
                      <StatisticValue>
                          {intlFormatMoneyWithCompact(summary.totalMissedRevenueUnavailable, user.getCompanyCurrency(), user.getCompanyLocale(), HUNDRED_THOUSAND)}
                      </StatisticValue>
                      <StatisticLabel>{t('unavailable_items.summary.total_revenue_unavailable')}</StatisticLabel>
                  </Statistic>

                  <Statistic
                    css={css`
                        grid-area: c;
                        align-self: start;
                        font-size: 8pt;
                    `}
                    color={summary.totalMissedRevenueSubstituted > 0 ? 'green' : undefined}
                    size="mini"
                  >
                      <StatisticValue>
                          {intlFormatMoneyWithCompact(summary.totalMissedRevenueSubstituted, user.getCompanyCurrency(), user.getCompanyLocale(), HUNDRED_THOUSAND)}
                      </StatisticValue>
                      <StatisticLabel>{t('unavailable_items.summary.total_revenue_substituted')}</StatisticLabel>
                  </Statistic>
              </>
          </CardContent>
        ) : (
          <NoUnavailableItemsCardContent/>
        ))}
        <CardExtra>
            <Link
              to={{
                  pathname: '/analytics/unavailable-items',
                  search: new URLSearchParams({
                      from,
                      to,
                  }).toString(),
              }}>
                Produits indisponibles
                <Icon name="arrow right"/>
            </Link>
        </CardExtra>
    </Card>)
}

export { UnavailableItemsSummaryCard }
